import { Box } from "../../box";
import { ButtonPresets } from "./ButtonPresets";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ClearInputGroupContext, InputGroup, useInputGroupProps } from "../../input-group";
import { CrossButton } from "../../button";
import { DateInputMask, useDateInput } from "./useDateInput";
import { Divider } from "../../divider";
import { HtmlInput } from "../../html";
import { Keys, augmentElement, cssModule, isNil, isNilOrEmpty, isNumber, mergeProps, omitProps, useAutoFocus, useControllableState, useDisposables, useEventCallback, useFocusWithin, useMergedRefs } from "../../shared";
import { MenuPresets } from "./MenuPresets";
import { useResponsiveValue } from "../../styling";
import { areEqualDates, toMidnightDate } from "./dateUtils";
import { useFieldInputProps } from "../../field";
import { useToolbarProps } from "../../toolbar";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "div";
const DateInput = /*#__PURE__*/forwardRef(({
  autoFocus,
  disabled,
  max,
  min,
  onChange,
  onDateChange,
  placeholder = "dd/mm/yyyy",
  readOnly,
  required,
  validationState,
  value,
  ...rest
}, ref) => {
  const inputRef = useMergedRefs(ref);
  useAutoFocus(inputRef, {
    delay: isNumber(autoFocus) ? autoFocus : undefined,
    isDisabled: !autoFocus || disabled || readOnly
  });
  const dateProps = useDateInput({
    forwardedRef: inputRef,
    max,
    min,
    onChange,
    onDateChange,
    value
  });
  return /*#__PURE__*/_jsx(HtmlInput, { ...mergeProps(rest, {
      "aria-invalid": validationState === "invalid" ? true : undefined,
      "aria-required": required ? true : undefined,
      className: "o-ui-date-range-input-date-input",
      disabled,
      placeholder,
      readOnly,
      ref: inputRef,
      type: "text"
    }, dateProps)
  });
});
const RangeInput = /*#__PURE__*/forwardRef((props, ref) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, isInField] = useFieldInputProps();
  const [inputGroupProps, isInGroup] = useInputGroupProps();
  const {
    active,
    as = "div",
    autoFocus,
    disabled,
    endDate,
    fluid,
    focus = false,
    hover,
    max,
    min,
    name,
    onBlur,
    onDatesChange,
    onFocus,
    placeholder,
    readOnly,
    required,
    startDate,
    validationState,
    ...rest
  } = mergeProps(props, inputGroupProps);
  const [hasFocus, setHasFocus] = useState(focus);
  const containerRef = useRef();
  const startDateRef = useRef();
  const endDateRef = useRef();
  const disposables = useDisposables();
  useImperativeHandle(ref, () => {
    const element = containerRef.current;

    element.focus = () => {
      var _startDateRef$current;

      (_startDateRef$current = startDateRef.current) === null || _startDateRef$current === void 0 ? void 0 : _startDateRef$current.focus();
    };

    return element;
  });
  const handleStartDateChange = useEventCallback((event, newDate) => {
    if (!isNil(newDate) && !isNil(endDate) && newDate > endDate) {
      newDate = endDate;
    }

    if (!isNil(onDatesChange)) {
      onDatesChange(event, newDate, endDate);
    }
  });
  const handleEndDateChange = useEventCallback((event, newDate) => {
    if (!isNil(newDate) && !isNil(startDate) && newDate < startDate) {
      newDate = startDate;
    }

    if (!isNil(onDatesChange)) {
      onDatesChange(event, startDate, newDate);
    }
  });
  const handleStartDateInputValueChange = useEventCallback(event => {
    if (event.target.value.length === DateInputMask.length) {
      // Defering because useDateInput is used in controlled mode and otherwise the value will not be updated when the value is clamped.
      disposables.requestAnimationFrame(() => {
        var _endDateRef$current;

        (_endDateRef$current = endDateRef.current) === null || _endDateRef$current === void 0 ? void 0 : _endDateRef$current.focus();
      });
    }
  });
  const handleEndDateInputValueChange = useEventCallback(event => {
    // @ts-ignore
    const newCharacter = event.nativeEvent.data; // If the new character is not a digit, we don't want to do anything since the new character will be removed by the mask input.
    // The digit is test with a regex because this is how our mask input third party is doing it and we want to be consistant.

    if (/\d/.test(newCharacter)) {
      if (isNilOrEmpty(event.target.value)) {
        var _startDateRef$current2;

        (_startDateRef$current2 = startDateRef.current) === null || _startDateRef$current2 === void 0 ? void 0 : _startDateRef$current2.focus();
      }
    }
  });
  const handleClearDates = useEventCallback(event => {
    // Deferring because otherwise the start date will not be blured which might result in not clearing the input properly.
    disposables.requestAnimationFrame(() => {
      startDateRef === null || startDateRef === void 0 ? void 0 : startDateRef.current.focus();
    });

    if (!isNil(onDatesChange)) {
      onDatesChange(event, null, null);
    }
  });
  const handleContainerKeyDown = useEventCallback(event => {
    if (event.key === Keys.esc) {
      event.preventDefault();
      handleClearDates(event);
    }
  });
  const handleEndDateKeyDown = useEventCallback(event => {
    if (event.key === Keys.backspace) {
      var _endDateRef$current2;

      if (isNilOrEmpty((_endDateRef$current2 = endDateRef.current) === null || _endDateRef$current2 === void 0 ? void 0 : _endDateRef$current2.value)) {
        var _startDateRef$current3;

        (_startDateRef$current3 = startDateRef.current) === null || _startDateRef$current3 === void 0 ? void 0 : _startDateRef$current3.focus();
      }
    }
  });
  const focusWithinProps = useFocusWithin({
    onBlur: useEventCallback(event => {
      setHasFocus(false);

      if (!isNil(onBlur)) {
        onBlur(event);
      }
    }),
    onFocus: useEventCallback(event => {
      setHasFocus(true);

      if (!isNil(onFocus)) {
        onFocus(event);
      }
    })
  });
  const hasValue = !isNil(startDate) || !isNil(endDate);
  return /*#__PURE__*/_jsxs(Box, { ...mergeProps(rest, {
      as,
      className: cssModule("o-ui-date-range-input", validationState, fluid && "fluid", disabled && "disabled", readOnly && "readonly", active && "active", hasFocus && "focus", hover && "hover", isInGroup && "in-group"),
      onKeyDown: handleContainerKeyDown,
      ref: containerRef,
      role: !isInField ? "group" : undefined
    }, focusWithinProps),
    children: [/*#__PURE__*/_jsx(DateInput, {
      autoFocus: autoFocus,
      disabled: disabled,
      max: max,
      min: min,
      name: !isNil(name) ? `${name}-start-date` : undefined,
      onChange: handleStartDateInputValueChange,
      onDateChange: handleStartDateChange,
      placeholder: placeholder,
      readOnly: readOnly,
      ref: startDateRef,
      required: required,
      validationState: validationState,
      value: startDate
    }), /*#__PURE__*/_jsx(Divider, {
      className: "o-ui-date-range-input-divider",
      orientation: "vertical"
    }), /*#__PURE__*/_jsx(DateInput, {
      disabled: disabled,
      max: max,
      min: min,
      name: !isNil(name) ? `${name}-end-date` : undefined,
      onChange: handleEndDateInputValueChange,
      onDateChange: handleEndDateChange,
      onKeyDown: handleEndDateKeyDown,
      placeholder: placeholder,
      readOnly: readOnly,
      ref: endDateRef,
      required: required,
      tabIndex: hasFocus ? 0 : -1,
      validationState: validationState,
      value: endDate
    }), hasValue && !disabled && !readOnly && /*#__PURE__*/_jsx(ClearInputGroupContext, {
      children: /*#__PURE__*/_jsx(CrossButton, {
        "aria-label": "Clear dates",
        className: "o-ui-date-range-input-clear-button",
        condensed: true,
        onClick: handleClearDates,
        size: "xs"
      })
    })]
  });
});
export function InnerDateRangeInput(props) {
  const [toolbarProps] = useToolbarProps();
  const [fieldProps] = useFieldInputProps();
  const [inputGroupProps, isInGroup] = useInputGroupProps();
  const {
    active,
    as = DefaultElement,
    autoFocus,
    defaultEndDate,
    defaultStartDate,
    disabled,
    endDate: endDateProp,
    fluid,
    focus = false,
    forwardedRef,
    hover,
    max,
    min,
    name,
    onBlur,
    onDatesChange,
    onFocus,
    placeholder,
    presets,
    presetsVariant = "compact",
    readOnly,
    required,
    startDate: startDateProp,
    validationState,
    ...rest
  } = mergeProps(props, omitProps(toolbarProps, ["orientation"]), fieldProps, inputGroupProps);
  const fluidValue = useResponsiveValue(fluid);
  const [startDate, setStartDate] = useControllableState(startDateProp, defaultStartDate, null);
  const [endDate, setEndDate] = useControllableState(endDateProp, defaultEndDate, null);
  const containerRef = useRef();
  const rangeRef = useRef();
  useImperativeHandle(forwardedRef, () => {
    // For presets, used the group container as the ref element.
    if (!isNil(presets)) {
      const element = containerRef.current;

      element.focus = () => {
        var _rangeRef$current;

        (_rangeRef$current = rangeRef.current) === null || _rangeRef$current === void 0 ? void 0 : _rangeRef$current.focus();
      };

      return element;
    }

    return rangeRef.current;
  });
  const applyDates = useCallback((event, newStartDate, newEndDate) => {
    if (!areEqualDates(startDate, newStartDate) || !areEqualDates(endDate, newEndDate)) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);

      if (!isNil(onDatesChange)) {
        onDatesChange(event, newStartDate, newEndDate);
      }
    }
  }, [onDatesChange, startDate, setStartDate, endDate, setEndDate]);
  const handleSelectPreset = useEventCallback((event, newIndex) => {
    const preset = presets[newIndex];

    if (!isNil(preset)) {
      applyDates(event, preset.startDate, preset.endDate);
    }
  });
  const presetsProps = useMemo(() => {
    if (!isNil(presets)) {
      const selectedIndex = presets.findIndex(x => areEqualDates(toMidnightDate(x.startDate), toMidnightDate(startDate)) && areEqualDates(toMidnightDate(x.endDate), toMidnightDate(endDate)));
      return {
        onSelectionChange: handleSelectPreset,
        selectedIndex: selectedIndex !== -1 ? selectedIndex : undefined,
        values: presets.map(x => x.text)
      };
    }

    return null;
  }, [presets, startDate, endDate, handleSelectPreset]);

  const rangeMarkup = /*#__PURE__*/_jsx(RangeInput, {
    active: active,
    autoFocus: autoFocus,
    disabled: disabled,
    endDate: endDate,
    fluid: fluidValue,
    focus: focus,
    hover: hover,
    max: max,
    min: min,
    name: name,
    onBlur: onBlur,
    onDatesChange: applyDates,
    onFocus: onFocus,
    placeholder: placeholder,
    readOnly: readOnly,
    ref: rangeRef,
    required: required,
    startDate: startDate,
    validationState: validationState
  });

  if (!isNil(presetsProps)) {
    return presetsVariant === "compact" ? /*#__PURE__*/_jsxs(InputGroup, { ...mergeProps(rest, {
        as,
        disabled,
        fluid: fluidValue,
        readOnly,
        ref: containerRef
      }),
      children: [rangeMarkup, /*#__PURE__*/_jsx(MenuPresets, { ...presetsProps
      })]
    }) : /*#__PURE__*/_jsxs(Box, { ...mergeProps(rest, {
        as,
        className: cssModule("o-ui-date-range-input-button-presets", fluidValue && "fluid"),
        ref: containerRef
      }),
      children: [rangeMarkup, !disabled && !readOnly && /*#__PURE__*/_jsx(ButtonPresets, { ...presetsProps
      })]
    });
  }

  return /*#__PURE__*/_jsx(ClearInputGroupContext, {
    children: augmentElement(rangeMarkup, mergeProps(rest, {
      as,
      className: isInGroup ? "o-ui-date-range-input-in-group" : undefined,
      ref: containerRef
    }))
  });
} // Cheating here because we want to mimick an input even it's a div.

InnerDateRangeInput.defaultElement = "input";
export const DateRangeInput = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerDateRangeInput, { ...props,
  forwardedRef: ref
}));